
































































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import { mixins } from "vue-class-component";

import VuePlotly from "@statnett/vue-plotly";
import buildUrl from "build-url";

import { endpoints } from "@/endpoints";
import { eventLoading } from "@/components/sce/EventLoading";
import { fmtFloat, fmtDate, fmtPct } from "./formatters";
import PlotlyResizeMixin from "./plotly-resize-mixin";

interface PanelState {
    loading: boolean;
    error: string | null;
    data: any[];
}
function newPanelState(): PanelState {
    return { loading: false, error: null, data: [] };
}

/**
 * Admin-only pipeline diagnostics. Hidden in RoadClosureAnalytics.vue behind the
 * ``auth/isAllowedSensitiveInfo`` getter, and its endpoints are gated server-side with
 * ``SensitiveInfoPermission`` (403 for users without ``view_sensitive_info``), so external
 * users get neither the tab nor the data.
 */
@Component({ components: { VuePlotly } })
export default class DataAdminTab extends mixins(PlotlyResizeMixin) {
    @Prop({ required: true }) startDate!: string;
    @Prop({ required: true }) endDate!: string;

    fmtFloat = fmtFloat;
    fmtDate = fmtDate;
    fmtPct = fmtPct;

    source: PanelState = newPanelState();
    states: PanelState = newPanelState();
    multiCategory: PanelState = newPanelState();
    /** Per-source-API daily rows — drives the failure-timeline heatmap. */
    daily: PanelState = newPanelState();

    mounted() {
        this.fetchAll();
    }

    resizeCharts() {
        this.refitPlotly(["heatmapChart"]);
    }

    /** One key over both fetch inputs so a date-range change triggers exactly one fetch. Stacked
     * @Watch decorators fire once per changed prop, so changing the range (startDate + endDate in
     * the same tick) used to fetch every panel twice. */
    private get fetchParamsKey(): string {
        return `${this.startDate}|${this.endDate}`;
    }

    @Watch("fetchParamsKey")
    onParamsChanged() {
        this.fetchAll();
    }

    fetchAll() {
        if (!this.startDate || !this.endDate) return;
        this.fetchPanel(
            this.source,
            endpoints.roadClosureAnalyticsSourceApiCoverageRollupUrl,
        );
        this.fetchPanel(
            this.states,
            endpoints.roadClosureAnalyticsStateRollupUrl,
        );
        this.fetchPanel(
            this.multiCategory,
            endpoints.roadClosureAnalyticsMultiCategorySourceClosuresUrl,
        );
        this.fetchPanel(
            this.daily,
            endpoints.roadClosureAnalyticsSourceApiCoverageDailyUrl,
        );
    }

    fetchPanel(panel: PanelState, url: string) {
        panel.loading = true;
        panel.error = null;
        eventLoading.startLoading();
        Vue.axios({
            url: buildUrl(url, {
                queryParams: {
                    start_date: this.startDate,
                    end_date: this.endDate,
                },
            }),
        })
            .then((response: any) => {
                panel.data = response.data || [];
            })
            .catch((err: any) => {
                console.error("[RCA Admin]", err);
                panel.error = err?.message || "Failed to load.";
                panel.data = [];
            })
            .finally(() => {
                panel.loading = false;
                eventLoading.stopLoading();
            });
    }

    get sourceRows(): any[] {
        return this.source.data;
    }
    get stateRows(): any[] {
        return this.states.data;
    }
    get multiCategoryRows(): any[] {
        return this.multiCategory.data;
    }

    sourceHeaders = [
        { text: "Source API", value: "source_api_key" },
        { text: "State", value: "state_code" },
        { text: "Source name", value: "source_name" },
        { text: "Selected days", value: "selected_day_count", align: "end" },
        { text: "Successful days", value: "success_day_count", align: "end" },
        { text: "Failed days", value: "failed_day_count", align: "end" },
        { text: "Success rate", value: "success_rate", align: "end" },
    ];

    stateHeaders = [
        { text: "State", value: "state_code" },
        {
            text: "Non-complete days",
            value: "non_complete_day_count",
            align: "end",
        },
        {
            text: "Closure events",
            value: "closure_event_count_sum",
            align: "end",
        },
        {
            text: "Closed segment-days",
            value: "closed_link_count_sum",
            align: "end",
        },
        {
            text: "Closed-segment km-days",
            value: "closed_length_km_sum",
            align: "end",
        },
        {
            text: "Impacted enterprises",
            value: "enterprise_impacted_count_sum",
            align: "end",
        },
        {
            // Endpoint-exposure grain: non-blocked impacted == rerouted.
            text: "Rerouted enterprises",
            value: "enterprise_non_blocked_count_sum",
            align: "end",
        },
        {
            text: "Blocked enterprises",
            value: "enterprise_blocked_count_sum",
            align: "end",
        },
    ];

    multiCategoryHeaders = [
        { text: "Date", value: "scenario_date_local" },
        { text: "State", value: "state_code" },
        { text: "Source closure id", value: "source_closure_id" },
        {
            text: "Category events",
            value: "category_event_count",
            align: "end",
        },
        { text: "Category ids", value: "closure_category_ids_csv" },
        {
            text: "Source record count",
            value: "total_source_record_count",
            align: "end",
        },
    ];

    /**
     * Pivot the per-source-API daily rows into a (state / source_api_key) × scenario_date matrix
     * of 0/1 failure flags.
     */
    get heatmapPivot(): { z: number[][]; x: string[]; y: string[] } | null {
        const rows = Array.isArray(this.daily.data) ? this.daily.data : [];
        if (!rows.length) return null;

        const dateSet = new Set<string>();
        const labels = new Set<string>();
        const cells = new Map<string, number>(); // key: `${label}__${date}`

        for (const r of rows) {
            const state = (r.state_code ?? "").toString();
            const api = (r.source_api_key ?? "").toString();
            const label = `${state} / ${api}`;
            const date = String(r.scenario_date_local).slice(0, 10);
            dateSet.add(date);
            labels.add(label);
            cells.set(`${label}__${date}`, r.failure_flag ? 1 : 0);
        }

        const dates = Array.from(dateSet).sort();
        const labelList = Array.from(labels).sort();
        const z = labelList.map((label) =>
            dates.map((date) => cells.get(`${label}__${date}`) ?? 0),
        );
        return { z, x: dates, y: labelList };
    }

    get heatmapTraces(): any[] {
        const p = this.heatmapPivot;
        if (!p) return [];
        return [
            {
                type: "heatmap",
                z: p.z,
                x: p.x,
                y: p.y,
                colorscale: [
                    [0.0, "#dcfce7"],
                    [0.5, "#fef3c7"],
                    [1.0, "#b91c1c"],
                ],
                showscale: false,
                zmin: 0,
                zmax: 1,
                hovertemplate:
                    "Source: %{y}<br>Date: %{x}<br>Failed: %{z}<extra></extra>",
            },
        ];
    }

    get heatmapLayout(): any {
        const p = this.heatmapPivot;
        const rowCount = p ? p.y.length : 1;
        const dates = p ? p.x : [];
        const targetLabels = 25;
        const step = Math.max(1, Math.ceil(dates.length / targetLabels));
        const tickvals = dates.filter((_d, i) => i % step === 0);
        const lastSample = dates[dates.length - 1] || "";
        const multiYear =
            (dates[0] || "").slice(0, 4) !== lastSample.slice(0, 4);
        const ticktext = tickvals.map((d) => {
            const dt = new Date(d + "T00:00:00");
            if (Number.isNaN(dt.getTime())) return d;
            return dt.toLocaleDateString("en-AU", {
                day: "2-digit",
                month: "short",
                year: multiYear ? "numeric" : undefined,
            });
        });
        return {
            dragmode: false,
            height: Math.max(320, 24 * rowCount + 100),
            margin: { l: 220, r: 60, t: 8, b: 100 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            xaxis: {
                type: "category",
                automargin: true,
                tickangle: -45,
                tickmode: "array",
                tickvals,
                ticktext,
                title: { text: "Scenario date", standoff: 24 },
            },
            yaxis: {
                automargin: true,
                autorange: "reversed",
                ticksuffix: "  ",
            },
        };
    }
}
